import { useEffect, useRef, useState } from "react";
import IApiResponse from "../../types/IApiResponse";
import { postData } from "../../services/api";
import apiUrls from "../../config/apiUrls";
import handleApiResponse from "../../utils/handleApiResponse";
import { useClubSearch } from "../../contexts/ClubSearchContext";
import { filterWomensFootball } from "../../utils/womensFootballFilter";

import LabelText from "../../ui/LabelText";
import { StyledHeader } from "../ClubDetail/ClubFinancial/ClubFinancial";
import { FilterSectionStyles, SortDropdown } from "../../styled-components/FilterStyles";
import { IClubSearchResonseData, IClubSearchResponse } from "../../types/IClubSearch";
import ClubDetails from "./ClubDetails";
import SecondaryButton from "../../ui/SecondaryButton";
import FilterSort from "../PlayerLanding/PlayerSearch/FilterSort";
import playerFilterData from "../../data/playerFilterData";
import PaginationComponent from "../../ui/PaginationComponent";
import { useLoading } from "../../contexts/LoadingContext";
import ClubFilterSection from "./ClubFilterSection";
import { useNotification } from "../../contexts/NotificationContext";
import PWNotification from "../PWNotification";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


interface IClubSearchResultsPage {
    onClearClubListClick?: () => void;
}

const clubSortData = ["Revenue", "Costs", "Profit / loss", "Country, League", "Debt", "Av. Player age", "Av. Player value", "Av. Wage pw",
    "Retained Earnings", "Intangible assets", "Receivables-Total", "Receivables-12-months", "Av. System fit", "League Position",
    "Expected league position", "Exp league pos / System fit", "Bookings this season", "Estimates receivables remaining for players"];

const ClubSearchResultPage: React.FC<IClubSearchResultsPage> = ({
    onClearClubListClick
}) => {
    const { totalRevenue, totalCost, totalProfitLoss, totalDebt, retainedEraning, debtVsAssetsRatio,
        totalIntangibleAssets, totalReceivables, clubRegionValues, receivablesPercentage, sortClubOrderName, sortClubOrdering,
        clubCurrentPage, setIsClubFilterChange, setClubCurrentPage, isClubFilterChange , avgSquadValue , avgSystemFit , avgPlayerAge , squadPurchaseCostVSValue ,includeFinancial , wholeSquad } = useClubSearch();
    const { showNotification, hideNotification, displayNotification, setDisplayNotification, notificationContent, setNotificationContent,
    } = useNotification();
    const [clubResults, setClubResult] = useState<IClubSearchResponse[]>([]);
    const [openIndex, setOpenIndex] = useState(0);
    const [isSortClicked, setIsSortClicked] = useState(false);
    const filterSortRef = useRef<HTMLDivElement | null>(null);
    const [clubResultsCount, setClubResultsCount] = useState(0);
    const totalPages = Math.ceil(clubResultsCount / 20);
    const { startLoading, stopLoading } = useLoading();

    const fetchClubSearchResults = async () => {
        try {
            startLoading();
            const areaValues = clubRegionValues.map((reg) => reg.optionId);
            const requestParameters = {
                "areaId": areaValues,
                "competitionId": null,
                ...(includeFinancial && {
                    "minTotalRevenue": totalRevenue.min,
                    "maxTotalRevenue": totalRevenue.max,
                    "minTotalCost": totalCost.min,
                    "maxTotalCost": totalCost.max,
                    "minTotalProfitLoss": totalProfitLoss.min,
                    "maxTotalProfitLoss": totalProfitLoss.max,
                    "minTotalDebt": totalDebt.min,
                    "maxTotalDebt": totalDebt.max,
                    "minRetainedEarnings": retainedEraning.min,
                    "maxRetainedEarnings":  retainedEraning.max,
                    // "minAssetsLiabilities": assetsLiabilities.min,
                    // "maxAssetsLiabilities": assetsLiabilities.max,
                    "minPurchaseVsMarketValue": wholeSquad.min,
                    "maxPurchaseVsMarketValue": wholeSquad.max,
                    "minDebtVsAssetsRatio": debtVsAssetsRatio.min,
                    "maxDebtVsAssetsRatio": debtVsAssetsRatio.max,
                    "minTotalIntangibleAssets": totalIntangibleAssets.min,
                    "maxTotalIntangibleAssets": totalIntangibleAssets.max,
                    "minTotalReceivables": totalReceivables.min,
                    "maxTotalReceivables": totalReceivables.max,
                    "minReceivablesPercentage": receivablesPercentage.min,
                    "maxReceivablesPercentage": receivablesPercentage.max,
                }),
                "minAvSquadValue": avgSquadValue.min,
                "maxAvSquadValue": avgSquadValue.max,
                "minAvSystemFit": avgSystemFit.min,
                "maxAvSystemFit": avgSystemFit.max,
                "minAvPlayerAge": avgPlayerAge.min,
                "maxAvPlayerAge": avgPlayerAge.max,
                // "minPurchaseVsMarketValue": 0,
                // "maxPurchaseVsMarketValue": 0,
                // "isPurchaseVsMarketValueWhole": true,
                "orderName": sortClubOrderName === 'Estimates receivables remaining for players' ? 'Receivables-Total' : sortClubOrderName,
                "ordering": sortClubOrdering,
                "pageNo": clubCurrentPage,
                "pageSize": 20
            };
            
            const response: IApiResponse<IClubSearchResonseData> = await postData(
                apiUrls.TeamsFilterSearch,
                requestParameters
            );
            handleApiResponse(
                response,
                (clubSearchResults) => {
                    stopLoading();
                    const filteredTeams = filterWomensFootball(clubSearchResults.teams || []);
                    if(filteredTeams.length > 0){
                        //setIsNotification(false);
                        setDisplayNotification(false);
                        setNotificationContent(null);
                        setIsClubFilterChange(false);
                        setClubResult(filteredTeams);
                        setClubResultsCount(filteredTeams.length);
                    }

                    else{
                        setClubResult([])
                        setDisplayNotification(true);
                        setNotificationContent(
                            <div className="notification">
                              <InfoOutlinedIcon className="icon" />
                              <p>
                                <LabelText className="squad-chart-tooltip notification-content">No results found -</LabelText>
                                <LabelText className="player-given-name notification-content"> Try adjusting the filters</LabelText>
                              </p>
                            </div>
                          );
                    }
                   
                },
                (error) => {
                    stopLoading();
                    console.error("Error fetching player search result data:", error);
                }
            );
        }
        catch (error) {
            stopLoading();
            console.error(error);
        }
    }

    const handleToggle = (index: number) => {
        setOpenIndex((prevIndex) => (prevIndex === index ? -1 : index));
    };

    const handlePageChange = (page: number) => {
        setIsClubFilterChange(true);
        setClubCurrentPage(page);
    };

    const renderClubResults = () => {
        return (
            <>
                {
                    clubResults?.map((clubData: IClubSearchResponse, index: number) => (
                        <div className="player-card-wrapper" key={clubData.teamId}>
                            <ClubDetails
                                clubData={clubData}
                                isOpen={openIndex === index}
                                onToggle={(event: React.MouseEvent) => {
                                    event.stopPropagation();
                                    handleToggle(index);
                                }}
                                //playerData={{ playerId: playerId, playerClubURL: playerClubURL }}
                                type="candidate"
                            />
                        </div>
                    ))
                }
                {clubResults?.length > 0 && (
                    <div className="pagination-wrapper">
                        <PaginationComponent
                            totalPages={totalPages}
                            currentPage={clubCurrentPage}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </>
        )
    }
    const handleSortChange = () => {
        setIsSortClicked(false);
    };


    useEffect(() => {
        if (isClubFilterChange) {
            fetchClubSearchResults();
        }

    }, [isClubFilterChange, totalRevenue, totalCost, totalProfitLoss, totalDebt, retainedEraning, debtVsAssetsRatio,
        totalIntangibleAssets, totalReceivables, clubRegionValues, clubCurrentPage, sortClubOrderName, sortClubOrdering])

    return (
        <>
            <FilterSectionStyles className="player-filters-section">
                <div className="header-content-wrapper">
                    <StyledHeader className="header-content">
                        <LabelText className="player-family-name">Filters</LabelText>
                    </StyledHeader>
                </div>
                <ClubFilterSection />

                {/* <PlayerFilterSection
                    ageStatistics={memoizedAgeStatistics}
                    ref={playerFilterSectionRef}
                    onSelectedAttributes={handleSelectedAttributes}
                    onSelectedTags={handleSelectedTags}
                /> */}
            </FilterSectionStyles>
            <div className="player-results-section">
                <div className="header-content-wrapper">
                    <StyledHeader className="header-content">
                        <LabelText className="player-family-name">Results</LabelText>
                        <div className="result-actions-btn">
                            <SecondaryButton
                                iconType="sort"
                                label={
                                    <>
                                        <span className="sort-label">Sort by: </span>
                                        <LabelText className="clear-labeltext">
                                            {sortClubOrderName}
                                        </LabelText>
                                    </>
                                }
                                onClick={() => setIsSortClicked((prev) => !prev)}
                            />
                            {isSortClicked && (
                                <SortDropdown ref={filterSortRef} className="newplayer-brop">
                                    <FilterSort
                                        onSortChange={handleSortChange}
                                        data={clubSortData}
                                        sortType="Club"
                                    />
                                </SortDropdown>
                            )}
                        </div>
                    </StyledHeader>
                </div>
                {/* {isPlayerResultsLoading && <div className="playerresults-loader">
                    <div className="loader-header-container">
                        <p>Creating list</p>
                        <img src={MachineFootballIcon} className="mf-logo" alt="machine-football-logo" />
                    </div>
                    <div className="loader-progressbar-container">
                        <BorderLinearProgress variant="determinate" value={loadingProgressValue} />
                    </div>
                    <div className="loader-subprocess">
                        {subProcesses[subProcessIndex]}
                        <p>
                            {loadingProgressValue}%
                        </p>
                    </div>
                </div>} */}
                {renderClubResults()}
                {displayNotification && (
                    <PWNotification
                        message={notificationContent}
                        setIsNotification={() => setDisplayNotification(false)}
                        isDisplayCloseIcon={true}
                        className="player-filter-notification club-noresult-notification"
                    />
                )}
            </div>


            {/* {loading && playerResults?.length > 0 && <LoadingSpinner />} */}
        </>
    );
};

export default ClubSearchResultPage;